import { useEffect, useRef, useState } from 'react';
import { IUseMicrophoneTestProps, RenderFrameType } from './interfaces';

export const useMicrophoneTest = ({
  activeDeviceId,
  permissionState,
}: IUseMicrophoneTestProps) => {
  const [volume, setVolume] = useState(0);
  // A ref (not a `let`) so the setTimeout closures created in the loop below
  // close over a stable binding, satisfying no-loop-func — mutating
  // `.current` still lets every pending timeout see the abort signal.
  const unmutedRef = useRef(false);

  useEffect(() => {
    unmutedRef.current = false;
    const timeoutIDs: ReturnType<typeof setInterval>[] = [];

    const setMicrophoneValue: RenderFrameType = ({
      analyser,
      bufferLength,
    }) => {
      const dataArray = new Uint8Array(bufferLength);

      requestAnimationFrame(() => {
        if (unmutedRef.current) {
          return;
        }

        setMicrophoneValue({ analyser, bufferLength });
      });

      analyser.getByteFrequencyData(dataArray);

      for (let i = 0; i < bufferLength; i++) {
        const timeoutID = setTimeout(() => {
          if (unmutedRef.current) {
            return;
          }

          // Compute the average volume from the frequency data
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

          // Scale the volume
          setVolume(Math.round(avg));
        }, 10);

        timeoutIDs.push(timeoutID);
      }
    };

    const startCheckMicro = async () => {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: activeDeviceId }, noiseSuppression: true },
        video: false,
      });

      const audioContext = new AudioContext({ sampleRate: 44100 }); // To be compatible with more devices
      const src = audioContext.createMediaStreamSource(mediaStream);
      const analyser = audioContext.createAnalyser();

      src.connect(analyser);
      analyser.fftSize = 256;

      const bufferLength = analyser.frequencyBinCount;

      setMicrophoneValue({ analyser, bufferLength });
    };

    if (permissionState === 'granted') {
      startCheckMicro();
    }

    return () => {
      unmutedRef.current = true;

      setTimeout(() => {
        timeoutIDs.map((timeoutID) => clearTimeout(timeoutID));
      }, 0);
    };
  }, [activeDeviceId, permissionState]);

  return { volume };
};
